import React, { useState } from 'react';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import { Menu, Store, LogOut, ExternalLink } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { NotificationBell } from './NotificationBell';
import { SoundGate } from './SoundGate';
import { Logo } from './Logo';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';

function SidebarNav({ groups, onNavigate }) {
  return (
    <nav className="flex flex-col gap-5">
      {groups.map((g) => (
        <div key={g.label}>
          <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-widest text-kelate-soft">{g.label}</div>
          <div className="flex flex-col gap-0.5">
            {g.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={onNavigate}
                data-testid={`sidenav-${item.label.toLowerCase().replace(/[\s/]+/g, '-')}`}
                className={({ isActive }) => `flex items-center justify-between rounded-sm px-3 py-2 text-sm transition-colors ${isActive ? 'bg-kelate-darkwood text-kelate-surface' : 'text-kelate-soft hover:bg-secondary hover:text-kelate-ink'}`}
              >
                <span className="flex items-center gap-2.5">
                  <item.icon size={15} />
                  {item.label}
                </span>
                {item.badge > 0 && (
                  <span data-testid={`sidenav-badge-${item.label.toLowerCase().replace(/[\s/]+/g, '-')}`} className="flex h-4 min-w-4 items-center justify-center rounded-full bg-kelate-red px-1 text-[10px] font-semibold text-white">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

export function CenterLayout({ title, subtitle, groups, recipientType, recipientId = null, fullNotifUrl, children }) {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-kelate-bg">
      <SoundGate />
      <div className="mx-auto flex max-w-[1440px]">
        <aside data-testid="center-sidebar" className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-kelate-line bg-kelate-surface lg:flex">
          <div className="border-b border-kelate-line px-5 py-5">
            <Link to="/" aria-label="KELATE home"><Logo className="h-7" /></Link>
            <div className="mt-0.5 text-[11px] uppercase tracking-widest text-kelate-soft">{title}</div>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-4">
            <SidebarNav groups={groups} />
          </div>
          <div className="border-t border-kelate-line p-3">
            <Link to="/" data-testid="view-storefront" className="flex items-center gap-2 rounded-sm px-3 py-2 text-xs text-kelate-soft hover:bg-secondary">
              <ExternalLink size={13} /> View storefront
            </Link>
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <header data-testid="center-topbar" className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-kelate-line bg-kelate-bg/95 px-4 backdrop-blur">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-btn" className="rounded-sm p-2 hover:bg-secondary lg:hidden"><Menu size={18} /></button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 bg-kelate-surface p-0">
                <div className="border-b border-kelate-line px-5 py-5">
                  <Logo className="h-7" />
                  <div className="mt-0.5 text-[11px] uppercase tracking-widest text-kelate-soft">{title}</div>
                </div>
                <div className="px-3 py-4">
                  <SidebarNav groups={groups} onNavigate={() => setOpen(false)} />
                </div>
              </SheetContent>
            </Sheet>
            <div className="min-w-0">
              <h1 className="truncate text-sm font-semibold text-kelate-ink">{subtitle || title}</h1>
            </div>
            <div className="ml-auto flex items-center gap-1">
              {recipientType && <NotificationBell recipientType={recipientType} recipientId={recipientId} fullPageUrl={fullNotifUrl} />}
              <div className="hidden sm:flex items-center gap-1.5 rounded-sm border border-kelate-line bg-kelate-surface px-2.5 py-1.5">
                <Store size={13} className="text-kelate-wood" />
                <span className="text-xs font-medium text-kelate-ink">{user?.name}</span>
              </div>
              <button
                data-testid="center-logout"
                onClick={async () => { await logout(); navigate('/'); }}
                className="rounded-sm p-2 text-kelate-soft hover:bg-secondary"
                title="Log out"
              >
                <LogOut size={16} />
              </button>
            </div>
          </header>
          <main className="p-4 lg:p-6">{children}</main>
        </div>
      </div>
    </div>
  );
}
